import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import ConfirmCreditsModal from './ConfirmCreditsModal';

interface CompanyCreditsProps {
  companyId: number;
  companyName: string;
  onClose: () => void;
}

interface RecruiterData {
  id: number;
  recruiter_id: number;
  email: string;
  name: string | null;
}

interface Credits {
  opportunities: number;
  highlights: number;
  confidentials: number;
}

const CompanyCredits: React.FC<CompanyCreditsProps> = ({ companyId, companyName, onClose }) => {
  const [recruiters, setRecruiters] = useState<RecruiterData[]>([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState<number | null>(null);
  const [credits, setCredits] = useState<Credits>({ opportunities: 0, highlights: 0, confidentials: 0 });
  const [formData, setFormData] = useState({
    opportunities: 0,
    highlights: 0,
    confidentials: 0,
    message: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    fetchRecruiters();
  }, [companyId]);

  useEffect(() => {
    if (selectedRecruiter) {
      fetchCredits();
    }
  }, [selectedRecruiter]);

  const fetchRecruiters = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/recruiters`);
      const recruitersData = response.data.recruiters || response.data || [];
      setRecruiters(Array.isArray(recruitersData) ? recruitersData : []);
    } catch (error: any) {
      toast.error('Erro ao carregar recrutadores');
      setRecruiters([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCredits = async () => {
    if (!selectedRecruiter) return;
    
    try {
      const response = await api.get(`/companies/${companyId}/credits/${selectedRecruiter}`);
      setCredits(response.data);
    } catch (error: any) {
      toast.error('Erro ao carregar créditos');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedRecruiter) {
      toast.error('Selecione um recrutador');
      return;
    }

    if (!formData.message.trim()) {
      toast.error('Mensagem é obrigatória');
      return;
    }

    if (!formData.opportunities && !formData.highlights && !formData.confidentials) {
      toast.error('Pelo menos um tipo de crédito deve ser informado');
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmChanges = async () => {
    try {
      setSubmitting(true);
      setShowConfirmModal(false);
      const recruiter = getSelectedRecruiter();

      await api.post(`/companies/${companyId}/credits/${selectedRecruiter}`, {
        ...formData,
        company_recruiter_id: recruiter?.id
      });
      toast.success('Créditos atualizados com sucesso!');
      setFormData({ opportunities: 0, highlights: 0, confidentials: 0, message: '' });
      fetchCredits();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar créditos');
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedRecruiter = () => {
    return recruiters.find(r => r.recruiter_id === selectedRecruiter);
  };

  const getSelectedRecruiterName = () => {
    const recruiter = getSelectedRecruiter();
    return recruiter ? (recruiter.name || recruiter.email) : '';
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Gerenciar Créditos - {companyName}</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>
        
        <div style={styles.content}>
          {loading ? (
            <div style={styles.loading}>Carregando...</div>
          ) : (
            <>
              <div style={styles.section}>
                <label style={styles.label}>Recrutador:</label>
                <select
                  value={selectedRecruiter || ''}
                  onChange={(e) => setSelectedRecruiter(Number(e.target.value) || null)}
                  style={styles.select}
                >
                  <option value="">Selecione um recrutador</option>
                  {Array.isArray(recruiters) && recruiters.map((recruiter) => (
                    <option key={recruiter.id} value={recruiter.recruiter_id}>
                      {recruiter.name || recruiter.email}
                    </option>
                  ))}
                </select>
              </div>

              {selectedRecruiter && (
                <>
                  <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Créditos Atuais</h3>
                    <div style={styles.creditsGrid}>
                      <div style={styles.creditItem}>
                        <span style={styles.creditLabel}>Vagas:</span>
                        <span style={styles.creditValue}>{credits.opportunities}</span>
                      </div>
                      <div style={styles.creditItem}>
                        <span style={styles.creditLabel}>Destaques:</span>
                        <span style={styles.creditValue}>{credits.highlights}</span>
                      </div>
                      <div style={styles.creditItem}>
                        <span style={styles.creditLabel}>Confidenciais:</span>
                        <span style={styles.creditValue}>{credits.confidentials}</span>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} style={styles.form}>
                    <h3 style={styles.sectionTitle}>Adicionar/Remover Créditos</h3>
                    
                    <div style={styles.inputGrid}>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Vagas:</label>
                        <input
                          type="number"
                          min="-20"
                          max="20"
                          value={formData.opportunities}
                          onChange={(e) => setFormData(prev => ({ ...prev, opportunities: Number(e.target.value) }))}
                          style={styles.numberInput}
                        />
                      </div>
                      
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Destaques:</label>
                        <input
                          type="number"
                          min="-20"
                          max="20"
                          value={formData.highlights}
                          onChange={(e) => setFormData(prev => ({ ...prev, highlights: Number(e.target.value) }))}
                          style={styles.numberInput}
                        />
                      </div>
                      
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Confidenciais:</label>
                        <input
                          type="number"
                          min="-20"
                          max="20"
                          value={formData.confidentials}
                          onChange={(e) => setFormData(prev => ({ ...prev, confidentials: Number(e.target.value) }))}
                          style={styles.numberInput}
                        />
                      </div>
                    </div>

                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Mensagem:</label>
                      <input
                        type="text"
                        placeholder="ex: Compra de T20 por boleto"
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        style={styles.textInput}
                        required
                      />
                    </div>

                    <div style={styles.actions}>
                      <button
                        type="submit"
                        disabled={submitting}
                        style={{
                          ...styles.submitBtn,
                          ...(submitting ? { backgroundColor: colors.textLight, cursor: 'not-allowed' } : {})
                        }}
                      >
                        {submitting ? 'Salvando...' : 'Salvar Créditos'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>
        
        <ConfirmCreditsModal
          isOpen={showConfirmModal}
          onConfirm={handleConfirmChanges}
          onCancel={() => setShowConfirmModal(false)}
          recruiterName={getSelectedRecruiterName()}
          changes={{
            opportunities: formData.opportunities,
            highlights: formData.highlights,
            confidentials: formData.confidentials
          }}
          currentCredits={credits}
          message={formData.message}
        />
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '1.5rem',
  },
  loading: {
    textAlign: 'center',
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  section: {
    marginBottom: '2rem',
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    color: colors.text,
    fontSize: '1.2rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
    color: colors.text,
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  creditsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
  },
  creditItem: {
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  creditLabel: {
    fontWeight: 'bold',
    color: colors.text,
  },
  creditValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: colors.primary,
  },
  form: {
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    padding: '1.5rem',
    backgroundColor: colors.background,
  },
  inputGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  inputGroup: {
    marginBottom: '1rem',
  },
  numberInput: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1.5rem',
    textAlign: 'center',
    fontFamily: 'inherit',
  },
  textInput: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1.5rem',
  },
  submitBtn: {
    padding: '0.75rem 2rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
  },
};

export default CompanyCredits;
